/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Link,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { getStore, RootState } from '@ink-ai/portal/reducers';
import LoadingButton from '@mui/lab/LoadingButton';
import { downloadFile } from '@ink-ai/portal/common/utils';
import { hasAnyRole, superAdminRolesForEnv, isDefaultProtectedName } from '@ink-ai/portal/common/authHelper';
import { StiType } from '@ink-ai/insight-service-sdk';
import { useNavigationListener } from '../../components/hooks/useNavigationListener';
import { referenceStoreCSVTemplate } from '../../components/reference-store/Template';
import {
  importReferenceStore,
  referenceStoreActions,
} from '@ink-ai/portal/reducers/reference-store';
import {
  FileUpload,
  FileUploadRef,
  FileUploadStatus,
} from '../../components/file-upload/FileUpload';
import { app } from '@ink-ai/portal/reducers/app';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';
import { PaperCard } from '../../components/Common';

const downloadCSVTemplate = () => {
  downloadFile(
    `\uFEFF${referenceStoreCSVTemplate}`, // Using BOM to force Excel to open CSV in UTF-8
    'referenceStore-template.csv',
    'text/csv',
  );
};

interface ImportReferenceStoreProps {
  referenceStoreUuid?: string;
  referenceStoreName?: string;
  referenceStoreDescription?: string;
}

const confirmExit = (skip: boolean) => {
  const referenceStore = getStore().getState().referenceStore;
  if (!skip && referenceStore.FileUploadStatus === FileUploadStatus.Uploading) {
    return 'The document is still in the process of uploading. Are you sure, you want to close?';
  }
};

export const ImportReferenceStore: React.FC<ImportReferenceStoreProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const {
    referenceStoreUuid,
    referenceStoreName,
    referenceStoreDescription,
    extractedFile,
    extractedFileName,
  } = location.state || {};
  const navigate = useNavigate();
  const referenceStore = useSelector(
    (state: RootState) => state.referenceStore,
  );
  const auth = useSelector((state: RootState) => state.auth);
  const isSuperAdmin = hasAnyRole(auth.group, superAdminRolesForEnv());
  const [name, setName] = useState(referenceStoreName);
  // Only super-admins may create/edit a "default"-named reference store. Block
  // it at the name field (the server also enforces this with a 403).
  const nameIsProtected = !isSuperAdmin && isDefaultProtectedName(name);
  const [description, setDescription] = useState(referenceStoreDescription);
  const uploadRef = useRef<FileUploadRef>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useNavigationListener();

  const skipLeaveConfirmRef = useRef(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !skipLeaveConfirmRef.current &&
      referenceStore.FileUploadStatus === FileUploadStatus.Uploading &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    window.onbeforeunload = () => confirmExit(skipLeaveConfirmRef.current) as any;
    return () => {
      window.onbeforeunload = null as any;
    };
  }, []);

  // 如果从提取任务带入了文件名，且当前 name 未设置，则用文件名（去除扩展名）作为默认名称
  useEffect(() => {
    if (!name && extractedFileName && typeof extractedFileName === 'string') {
      const base = extractedFileName.replace(/\.[^/.]+$/, '');
      setName(base);
    }
  }, [extractedFileName]);

  const handleCancel = () => {
    navigate('/reference-management');
  };

  useEffect(() => {
    // Clean up on unmount
    return () => {
      dispatch(referenceStoreActions.clearAll());
    };
  }, []);

  const handleUploadComplete = useCallback(async () => {}, []);

  const handleUploadError = useCallback(
    (error: Error) => {
      dispatch(
        referenceStoreActions.setFileUploadStatus({
          status: FileUploadStatus.Error,
        }),
      );

      dispatch(
        app.actions.setGlobalMessage({
          message: error.message || 'Failed to upload reference store file.',
          status: 'error',
        }),
      );

      dispatch(referenceStoreActions.setLoading(false));
    },
    [dispatch],
  );

  const handleStatusChange = useCallback(
    (status: FileUploadStatus) => {
      dispatch(
        referenceStoreActions.setFileUploadStatus({
          status,
        }),
      );
    },
    [dispatch],
  );

  const handleFileRemove = useCallback(() => {
    dispatch(
      referenceStoreActions.setFileUploadStatus({
        status: FileUploadStatus.WaitingUpload,
      }),
    );
  }, [dispatch]);

  const handleStartFileUpload = async () => {
    if (!selectedFile) {
      dispatch(
        app.actions.setGlobalMessage({
          message: 'Please select a file to upload.',
          status: 'error',
        }),
      );
      return;
    }
    try {
      dispatch(referenceStoreActions.setFileUploadStatus({ status: FileUploadStatus.Uploading }));
      // Step 1: create or update external reference store to get uploadUrl
      let externalUuid = referenceStoreUuid;
      let uploadUrl: string | undefined;
      if (externalUuid) {
        const resp = await makeRequest<any>({
          method: 'PUT',
          url: `/api/v1/referenceStore/external/${externalUuid}`,
          data: { name, desc: description },
          responseType: 'json',
        });
        uploadUrl = resp?.uploadUrl;
      } else {
        const resp = await makeRequest<any>({
          method: 'POST',
          url: `/api/v1/referenceStore/external`,
          data: { name, desc: description },
          responseType: 'json',
        });
        externalUuid = resp?.uuid;
        uploadUrl = resp?.uploadUrl;
      }
      if (!externalUuid || !uploadUrl) {
        throw new Error('Failed to initialize external reference store upload');
      }
      await axios.put(uploadUrl, selectedFile, {
        headers: { 'Content-Type': selectedFile.type || 'application/octet-stream' },
      });
      // Step 3: trigger import start
      await makeRequest<any>({
        method: 'POST',
        url: `/api/v1/referenceStore/external/${externalUuid}/start`,
        responseType: 'json',
      });
      dispatch(referenceStoreActions.setFileUploadStatus({ status: FileUploadStatus.Importing }));
      dispatch(referenceStoreActions.setLoading(true));
      skipLeaveConfirmRef.current = true;
      navigate('/reference-management');
    } catch (error: any) {
      dispatch(referenceStoreActions.setFileUploadStatus({ status: FileUploadStatus.Error }));
      dispatch(referenceStoreActions.setLoading(false));
      const backendMessage = error?.response?.data?.message || error?.message || 'Failed to import reference store';
      dispatch(app.actions.setGlobalMessage({ message: backendMessage, status: 'error' }));
    }
  };
  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Dialog open={blocker.state === 'blocked'}>
        <DialogTitle>{t('referenceStore.import.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('referenceStore.import.dialog.content')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.proceed()}>
            {t('referenceStore.import.dialog.leave')}
          </Button>
          <Button
            onClick={() => blocker.reset()}
            color="primary"
            variant="contained"
            autoFocus
          >
            {t('referenceStore.import.dialog.stay')}
          </Button>
        </DialogActions>
      </Dialog>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          color="inherit"
          onClick={() => navigate('/reference-management')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          {t('referenceStore.navigation.referenceManagement')}
        </Link>
        <Typography color="textPrimary">
          {t('referenceStore.navigation.import')}
        </Typography>
      </Breadcrumbs>
      <PaperCard>
        <Typography variant="h5" sx={{ textAlign: 'left' }}>
          {referenceStoreUuid
            ? t('referenceStore.import.title.update')
            : t('referenceStore.import.title.import')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: 'left' }}
        >
          {referenceStoreUuid
            ? t('referenceStore.import.description.update')
            : t('referenceStore.import.description.import')}
        </Typography>
        <Box sx={{ width: '50%' }}>
          <TextField
            label={t('referenceStore.import.form.name')}
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={name}
            error={nameIsProtected}
            helperText={nameIsProtected ? t('referenceStore.import.form.defaultNameForbidden') : undefined}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label={t('referenceStore.import.form.description')}
            variant="outlined"
            fullWidth
            sx={{ mb: 1 }}
            value={description}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Typography variant="body2" sx={{ mb: 1, textAlign: 'left' }}>
            {t('referenceStore.import.template.download')}{' '}
            <Link href="#" onClick={downloadCSVTemplate}>
              {t('referenceStore.import.template.csv')}
            </Link>
          </Typography>
          <FileUpload
            ref={uploadRef}
            accept=".csv"
            ownerType={StiType.ReferenceStore}
            initialFile={extractedFile}
            onFileSelect={(f) => setSelectedFile(f)}
            onStatusChange={handleStatusChange}
            onFileRemove={handleFileRemove}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-start',
          }}
        >
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleStartFileUpload}
            disabled={!name || nameIsProtected || referenceStore.loading}
            sx={{ width: '99px' }}
            loading={
              referenceStore.loading ||
              [FileUploadStatus.Uploading, FileUploadStatus.Importing].includes(
                referenceStore.FileUploadStatus,
              )
            }
          >
            {t(
              referenceStoreUuid
                ? 'referenceStore.actions.update'
                : 'referenceStore.actions.import',
            )}
          </LoadingButton>
          <Button onClick={handleCancel} sx={{ ml: 1, width: '99px' }}>
            {t('referenceStore.actions.cancel')}
          </Button>
        </Box>
      </PaperCard>

      
    </Container>
  );
};
